import { useEffect, useState } from 'react';
import { api, type ProcedureReport as Report } from '../api';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Skeleton } from '../components';
import { PeriodPicker, initialPeriod, type Period } from '../components/PeriodPicker';
import { formatMinor } from '../money';
import { describe } from '../errors';

/**
 * What the clinic actually does, by procedure code.
 *
 * The catalogue code is the clinic's own SKU for a procedure, and this is the
 * question it exists to answer: over a month, a quarter, a half or a year,
 * which procedures were performed most, by whom, and what did they bill.
 *
 * The page is built around one thing being true: COUNT AND VALUE ARE DIFFERENT
 * RANKINGS. Examinations are almost always the most frequent procedure and
 * almost never the most valuable; a single "top procedure" figure would hide
 * whichever of the two you actually needed. So they are two lists, side by
 * side, and neither is called "the top procedure".
 *
 * Completed work only. A planned treatment is an intention, and counting
 * intentions as activity is how a utilisation report starts flattering the
 * clinic that reads it.
 */
export function ProcedureReport({ clinicId }: { clinicId: string }) {
  const { t, locale, tx, formatNumber } = useI18n();
  const [period, setPeriod] = useState<Period>(() => initialPeriod('quarter'));
  const [dentist, setDentist] = useState('');
  const [category, setCategory] = useState('');
  const [dentists, setDentists] = useState<{ membershipId: string; name: string }[]>([]);
  const [data, setData] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .dentists()
      .then((r) => setDentists(r.dentists.map((d) => ({ membershipId: d.membershipId, name: d.name }))))
      .catch(() => setDentists([]));
  }, [clinicId]);

  useEffect(() => {
    setData(null);
    setError(null);
    api
      .procedureReport({
        grain: period.grain === 'custom' ? undefined : period.grain,
        from: period.from,
        to: period.to,
        dentist: dentist || undefined,
      })
      .then(setData)
      .catch((e) => setError(describe(e, t)));
  }, [clinicId, period, dentist, t]);

  if (error) return <ErrorState message={error} />;

  const categories = [...new Set((data?.procedures ?? []).map((p) => p.category).filter(Boolean))].sort() as string[];
  const rows = category ? (data?.procedures ?? []).filter((p) => p.category === category) : (data?.procedures ?? []);
  const byValue = category ? [...rows].sort((a, b) => b.billedMinor - a.billedMinor).slice(0, 10) : (data?.byValue ?? []);
  const totals = category
    ? {
        performed: rows.reduce((t2, p) => t2 + p.performed, 0),
        billedMinor: rows.reduce((t2, p) => t2 + p.billedMinor, 0),
        distinctCodes: rows.filter((p) => p.code !== null).length,
        uncoded: rows.find((p) => p.code === null)?.performed ?? 0,
      }
    : data?.totals;

  return (
    <>
      <div className="toolbar">
        <PeriodPicker value={period} onChange={setPeriod} />

        <select
          className="select-inline"
          value={dentist}
          onChange={(e) => setDentist(e.target.value)}
          aria-label={t.reports.perDentist}
        >
          <option value="">{t.reports.allDentists}</option>
          {dentists.map((d) => (
            <option key={d.membershipId} value={d.membershipId}>
              {d.name}
            </option>
          ))}
        </select>

        {categories.length > 1 ? (
          <select
            className="select-inline"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label={t.finance.category}
          >
            <option value="">{t.inventory.allCategories}</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {!data ? (
        <div className="card">
          <Skeleton rows={6} />
        </div>
      ) : totals!.performed === 0 ? (
        <div className="card">
          <EmptyState title={t.reports.noData} hint={t.reports.completedOnly} />
        </div>
      ) : (
        <>
          <div className="stats">
            <Tile
              label={t.reports.performed}
              value={formatNumber(totals!.performed)}
              hint={t.reports.completedOnly}
              tone="teal"
            />
            <Tile
              label={t.reports.billed}
              value={formatMinor(totals!.billedMinor, data.currency, locale)}
              hint={t.reports.billedHint}
              tone="blue"
            />
            <Tile
              label={t.reports.distinctCodes}
              value={formatNumber(totals!.distinctCodes)}
              tone="violet"
            />
            <Tile
              label={t.reports.uncoded}
              value={formatNumber(totals!.uncoded)}
              hint={totals!.uncoded > 0 ? tx(t.reports.uncodedNote, { n: totals!.uncoded }) : undefined}
              tone="amber"
            />
          </div>

          <div className="notice" style={{ marginBottom: 18 }}>
            <span>{t.reports.rankNote}</span>
          </div>

          <div className="split">
            <div className="card">
              <div className="card-head">
                <h2>{t.reports.byCount}</h2>
              </div>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>{t.settings.procedureCode}</th>
                      <th>{t.reports.performed}</th>
                      <th>{t.finance.uniquePatients}</th>
                      <th>{t.reports.billed}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => (
                      <tr key={p.code ?? 'uncoded'}>
                        <td>
                          <strong>
                            {p.code === null ? t.reports.uncoded : locale === 'ar' ? p.nameAr : p.nameEn}
                          </strong>
                          {p.code ? <span className="sub ltr-value">{p.code}</span> : null}
                        </td>
                        <td className="ltr-value">{formatNumber(p.performed)}</td>
                        <td className="ltr-value">{formatNumber(p.uniquePatients)}</td>
                        <td className="ltr-value">{formatMinor(p.billedMinor, data.currency, locale)}</td>
                        <td style={{ width: 90 }}>
                          {/* Drawn from the same rows, so the bar cannot
                              disagree with the number beside it. */}
                          <span className="rank-bar">
                            <span
                              style={{
                                width: `${Math.round((p.performed / (rows[0]?.performed || 1)) * 100)}%`,
                              }}
                            />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="card">
                <div className="card-head">
                  <h2>{t.reports.byValue}</h2>
                </div>
                <div className="table-scroll">
                  <table>
                    <tbody>
                      {byValue.map((p) => (
                        <tr key={p.code ?? 'uncoded'}>
                          <td>
                            <strong>
                              {p.code === null ? t.reports.uncoded : locale === 'ar' ? p.nameAr : p.nameEn}
                            </strong>
                            <span className="sub">
                              {formatNumber(p.performed)} × {t.reports.performed.toLowerCase()}
                            </span>
                          </td>
                          <td className="ltr-value" style={{ textAlign: 'end' }}>
                            {formatMinor(p.billedMinor, data.currency, locale)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>{t.reports.perDentist}</h2>
                </div>
                {data.byDentist.length === 0 ? (
                  <EmptyState title={t.dentists.noDentists} />
                ) : (
                  <div className="table-scroll">
                    <table>
                      <tbody>
                        {data.byDentist.map((d) => (
                          <tr key={d.membershipId}>
                            <td>
                              <strong>{d.name}</strong>
                              <span className="sub">
                                {formatNumber(d.distinctProcedures)} {t.reports.distinctCodes.toLowerCase()}
                              </span>
                            </td>
                            <td className="ltr-value">{formatNumber(d.performed)}</td>
                            <td className="ltr-value" style={{ textAlign: 'end' }}>
                              {formatMinor(d.billedMinor, data.currency, locale)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Tile({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone: string }) {
  return (
    <div className="card stat">
      <div className="stat-top">
        <span>{label}</span>
        <span className={`stat-icon ${tone}`} aria-hidden="true">
          ●
        </span>
      </div>
      <div className="stat-value ltr-value">{value}</div>
      {hint ? <div className="stat-hint">{hint}</div> : null}
    </div>
  );
}

